import React, { useState, useRef, useEffect } from 'react';

interface CustomDatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  'data-testid'?: string;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date...',
  className = '',
  disabled = false,
  label,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const parseLocalDate = (val: string): Date | null => {
    if (!val) return null;
    const parts = val.split('-');
    if (parts.length !== 3) return null;
    const [y, m, d] = parts.map(Number);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
    return new Date(y, m - 1, d);
  };

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? parseLocalDate(value) : null
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    selectedDate || new Date()
  );
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected date when value prop changes
  useEffect(() => {
    if (value) {
      const date = parseLocalDate(value);
      setSelectedDate(date);
      if (date) {
        setCurrentMonth(date);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return placeholder;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatValueDate = (date: Date): string => {
    // Format as YYYY-MM-DD in local time to avoid timezone offsets
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today for comparison
    
    // Prevent selecting future dates
    if (newDate > today) {
      return; // Don't select future dates
    }
    
    setSelectedDate(newDate);
    const formattedDate = formatValueDate(newDate);
    onChange(formattedDate);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDate(null);
    onChange('');
    setIsOpen(false);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Add year navigation
  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setFullYear(prev.getFullYear() - 1);
      } else {
        newMonth.setFullYear(prev.getFullYear() + 1);
      }
      return newMonth;
    });
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today);
    const formattedDate = formatValueDate(today);
    onChange(formattedDate);
    setIsOpen(false);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const today = new Date();
    
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-8 w-full" />
      );
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && 
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentMonth.getMonth() &&
        selectedDate.getFullYear() === currentMonth.getFullYear();
      
      const isToday = today.getDate() === day &&
        today.getMonth() === currentMonth.getMonth() &&
        today.getFullYear() === currentMonth.getFullYear();

      const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const isFuture = dayDate > todayEnd;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateSelect(day)}
          disabled={isFuture}
          className={`h-8 w-full text-xs rounded-md transition-colors flex items-center justify-center ${
            isFuture
              ? 'text-gray-500 cursor-not-allowed'
              : isSelected
              ? 'bg-orange-500 text-white font-medium'
              : isToday
              ? 'bg-orange-500/20 text-orange-400 font-medium'
              : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="input-label">
          {label}
        </label>
      )}
      
      <div className="w-full relative" ref={dropdownRef}>
        {/* Input Container */}
        <div className="relative flex">
          {/* Main Trigger Button */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            data-testid={dataTestId}
            className={`flex-1 bg-[#0c0c0c] border border-white/[0.1] rounded-lg px-3 py-2 text-white focus:outline-none ring-accent focus:outline-none flex items-center justify-between hover:bg-white/[0.06] transition-all ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${selectedDate && !disabled ? 'rounded-r-none border-r-0' : ''}`}
          >
            <span className={`truncate text-left ${!selectedDate ? 'text-gray-400' : ''}`}>
              {formatDisplayDate(selectedDate)}
            </span>
            <svg
              className={`w-4 h-4 transition-transform text-orange-400 ml-2 ${isOpen ? 'rotate-180' : ''}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          
          {/* Clear Button */}
          {selectedDate && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="bg-[#161616]/50 backdrop-blur-sm border border-white/[0.10] border-l-0 rounded-r-lg px-2 text-gray-400 hover:text-red-400 hover:bg-white/[0.06] transition-all focus:outline-none ring-accent focus:outline-none"
              title="Clear date"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>

        {/* Dropdown Calendar */}
        {isOpen && (
          <div className="absolute left-0 mt-1 bg-[#0c0c0c] border border-white/[0.1] rounded-lg shadow-xl z-[99999] p-3 w-64 md:w-72">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => navigateYear('prev')}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title="Previous Year"
                data-testid="datepicker-prev-year"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 15.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 111.414 1.414L11.414 10l3.293 3.293a1 1 0 010 1.414zM10.707 15.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 111.414 1.414L7.414 10l3.293 3.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => navigateMonth('prev')}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title="Previous Month"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="text-white font-medium text-sm">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </div>
              <button
                type="button"
                onClick={() => navigateMonth('next')}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title="Next Month"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => navigateYear('next')}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title="Next Year"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 4.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 11-1.414-1.414L8.586 10 5.293 6.707a1 1 0 010-1.414zM9.293 4.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 11-1.414-1.414L12.586 10 9.293 6.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="h-6 text-xs text-gray-400 text-center font-medium flex items-center justify-center">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-3">
              {renderCalendar()}
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-white/[0.10]">
              <button
                type="button"
                onClick={handleToday}
                className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
              >
                Today
              </button>
              {selectedDate && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 